import { createContract } from '@exodus/ethereum-lib'
import { bufferToHex } from '@exodus/ethereumjs/util'
import { retry } from '@exodus/simple-retry'

import { getServerByName } from '../../exodus-eth-server/index.js'

// TODO: Shouldn't this be a function of `ethereumStakingState.minDelegateAmount`?
const MIN_AMOUNT = '0.1'
const RETRY_DELAYS = ['10s']

export class EthereumStaking {
  static addresses = {
    ethereum: {
      EVERSTAKE_ADDRESS_CONTRACT_ACCOUNTING: '0x7a7f0b3c23C23a31cFcb0c44709be70d4D545c6e',
      EVERSTAKE_ADDRESS_CONTRACT_POOL: '0xD523794C879D9eC028960a231F866758e405bE34',
    },
    ethereumgoerli: {
      EVERSTAKE_ADDRESS_CONTRACT_ACCOUNTING: '0x6e95818C2Dde3d87406F5C5d0A759d9372053a94',
      EVERSTAKE_ADDRESS_CONTRACT_POOL: '0x1F28aD3B3e26192a09c1248D4092c692c32f8Ec0',
    },
    ethereumholesky: {
      EVERSTAKE_ADDRESS_CONTRACT_ACCOUNTING: '0x624087DD1904ab122A32878Ce9e933C7071F53B9',
      EVERSTAKE_ADDRESS_CONTRACT_POOL: '0xAFA848357154a6a624686b348303EF9a13F63264',
    },
  }

  static METHODS_IDS = {
    DELEGATE: '0x3a29dbae', // stake(uint256 source)
    UNSTAKE: '0x76ec871c', // unstake(uint256 amount)
    UNSTAKE_PENDING: '0xed0723d4', // unstakePending(uint256 amount)
    CLAIM_UNSTAKE: '0x33986ffa', // claimWithdrawRequest(uint256 amount)
    DEPOSIT: '0x47e7ef24', // deposit(address staker, uint256 amount)
  }

  static isDelegationTransactonCalldata = (txInput) => {
    const txInputHex = (Buffer.isBuffer(txInput) ? bufferToHex(txInput) : txInput) || '0x'
    if (typeof txInputHex !== 'string') throw new Error('expected string')

    return txInputHex.toLowerCase().startsWith(EthereumStaking.METHODS_IDS.DELEGATE)
  }

  constructor(
    asset, // ethereum or ethereumholesky for testnet
    minAmount = MIN_AMOUNT,
    server
  ) {
    this.asset = asset
    const accountingAddress =
      EthereumStaking.addresses[asset.name].EVERSTAKE_ADDRESS_CONTRACT_ACCOUNTING
    const poolAddress = EthereumStaking.addresses[asset.name].EVERSTAKE_ADDRESS_CONTRACT_POOL

    this.contractAccounting = createContract(accountingAddress, 'ethStakingAccounting')
    this.contractPool = createContract(poolAddress, 'ethStakingPool')
    this.accountingAddress = accountingAddress
    this.poolAddress = poolAddress
    this.minAmount = asset.currency.defaultUnit(minAmount)
    this.server = server
  }

  #buildTxData = (contract, method, ...args) => {
    return contract[method].build(...args)
  }

  #callReadFunctionContract = (contract, method, ...args) =>
    this.#callReadFunctionContractFrom(contract, method, undefined, ...args)

  #callReadFunctionContractFrom = (contract, method, from, ...args) => {
    const callData = this.#buildTxData(contract, method, ...args)
    const data = {
      data: bufferToHex(callData),
      to: contract.address,
      tag: 'latest',
    }

    if (typeof from === 'string' && from.length > 0) data.from = from

    const eth = this.server || getServerByName(this.asset.name)
    return retry((...args) => eth.ethCall(...args), { delayTimesMs: RETRY_DELAYS })(data)
  }

  // === ACCOUNTING ===

  /** Return total deposited and activated pool balance */
  async poolsBalance() {
    const totalPoolBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'balance'
    )
    return this.asset.currency.baseUnit(totalPoolBalance)
  }

  /** Return pool pending balance. Always < 32 ETH */
  async poolPendingBalance() {
    const pendingBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'pendingBalance'
    )
    return this.asset.currency.baseUnit(pendingBalance)
  }

  /** Return user pending balance (waiting to be added from the Pool contract to the ETH2 validator) */
  async pendingBalanceOf(address) {
    let pendingBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'pendingBalanceOf',
      address
    )
    pendingBalance = this.contractAccounting.pendingBalanceOf.parse(pendingBalance)
    return this.asset.currency.baseUnit(pendingBalance[0])
  }

  /** Return total user autocompound balance. Part of this balance could be in pending state after rewards autocompound */
  async autocompoundBalanceOf(address) {
    const userBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'autocompoundBalanceOf',
      address
    )
    return this.asset.currency.baseUnit(userBalance)
  }

  /** Return user pending deposited balance. Balance which deposited into validator but not active yet. Pending deposited balance can't be unstake till validator activation */
  async pendingDepositedBalanceOf(address) {
    const userBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'pendingDepositedBalanceOf',
      address
    )
    return this.asset.currency.baseUnit(userBalance)
  }

  /** Return user active origin deposited balance */
  async depositedBalanceOf(address) {
    const depositedBalance = await this.#callReadFunctionContract(
      this.contractAccounting,
      'depositedBalanceOf',
      address
    )
    return this.asset.currency.baseUnit(depositedBalance)
  }

  /* Get earned Rewards */
  async getLiquidRewards(address) {
    const [compoundBalance, depositedBalance] = await Promise.all([
      this.autocompoundBalanceOf(address),
      this.depositedBalanceOf(address),
    ])
    return compoundBalance.sub(depositedBalance)
  }

  /** Return withdrawable unstaked amount  */
  async withdrawRequest(address) {
    let amounts = await this.#callReadFunctionContract(
      this.contractAccounting,
      'withdrawRequest',
      address
    )
    amounts = this.contractAccounting.withdrawRequest.parse(amounts)
    return {
      requested: this.asset.currency.baseUnit(amounts[0]),
      readyForClaim: this.asset.currency.baseUnit(amounts[1]),
      canSendClaimRequest: amounts[0] !== '0' && amounts[0] === amounts[1],
    }
  }

  // === POOL ===

  /** Determine the effects of a deposit operation by mocking a transaction from the Everstake Pool contract, which can be used to estimate slippage. */
  async getDepositEffects(depositorAddress, amount) {
    const { address: from } = this.contractPool
    const prankDepositReturnData = await this.#callReadFunctionContractFrom(
      this.contractAccounting,
      'deposit',
      from,
      depositorAddress,
      amount.toBaseString()
    )
    const [interchangedAmount, activatedSlots] =
      this.contractAccounting.deposit.parse(prankDepositReturnData)

    return {
      interchangedAmount: this.asset.currency.baseUnit(interchangedAmount),
      activatedSlots: BigInt(activatedSlots),
    }
  }

  async stake({ amount, source = '2' }) {
    if (amount.gte(this.minAmount)) {
      return {
        to: this.poolAddress,
        amount,
        data: bufferToHex(this.#buildTxData(this.contractPool, 'stake', source)), // tx data field
      }
    }

    throw new Error(`Min Amount ${this.minAmount}`)
  }

  /* Unstake funds that are staked into the validator. Once unstaked they need to be claimed (once ready to withdraw) */
  async unstake({ address, amount, allowedInterchangeNum = 0, source = '2' }) {
    const amountWei = amount.toBaseString()
    const balance = await this.autocompoundBalanceOf(address) // amount staked into the validator (active balance)

    if (balance.gte(amount)) {
      return {
        to: this.poolAddress,
        amount: this.asset.currency.ZERO,
        data: bufferToHex(
          this.#buildTxData(this.contractPool, 'unstake', amountWei, allowedInterchangeNum, source)
        ),
      }
    }

    throw new Error(`Max Amount for unstake ${balance}`)
  }

  /** Claim funds requested by unstake (in withdraw state) */
  async claimWithdrawRequest({ address }) {
    try {
      const rewards = await this.withdrawRequest(address)
      if (rewards.canSendClaimRequest) {
        return {
          to: this.accountingAddress,
          amount: this.asset.currency.ZERO,
          data: bufferToHex(this.#buildTxData(this.contractAccounting, 'claimWithdrawRequest')),
        }
      }

      return null // no amount to withdraw
    } catch (error) {
      throw new Error(error)
    }
  }

  /** Unstake pending funds, that are still not staked into a Validator */
  async unstakePending({ address, amount }) {
    const pendingBalance = await this.pendingBalanceOf(address)

    if (amount.lte(pendingBalance)) {
      try {
        const amountWei = amount.toBaseString()
        return {
          to: this.poolAddress,
          amount: this.asset.currency.ZERO,
          data: bufferToHex(this.#buildTxData(this.contractPool, 'unstakePending', amountWei)),
        }
      } catch (err) {
        throw new Error(err)
      }
    } else {
      throw new Error(`Min Amount ${this.minAmount}`)
    }
  }
}
